import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Users, Key, ChevronRight, Check, X, 
  RefreshCw, Loader2, AlertCircle, UserPlus,
  Crown, Briefcase, Wrench, Eye, Headphones, Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { rbacService, authService } from '../services';

// Role icons mapping
const ROLE_ICONS = {
  super_admin: Crown,
  finance: Calculator,
  technical_admin: Wrench,
  partner: Briefcase,
  tenant_admin: Shield,
  accountant: Calculator,
  supporter: Headphones,
  viewer: Eye
};

// Role colors - solid backgrounds for better visibility
const ROLE_COLORS = {
  super_admin: 'bg-red-50 text-red-700 border-red-200',
  finance: 'bg-green-50 text-green-700 border-green-200',
  technical_admin: 'bg-purple-50 text-purple-700 border-purple-200',
  partner: 'bg-blue-50 text-blue-700 border-blue-200',
  tenant_admin: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  accountant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  supporter: 'bg-amber-50 text-amber-700 border-amber-200',
  viewer: 'bg-slate-50 text-slate-700 border-slate-200'
};

export const RolesPanel = () => {
  const [roles, setRoles] = useState({ global_roles: [], tenant_roles: [] });
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await rbacService.getRoles();
      setRoles(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Rollen');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleRoleClick = async (roleId) => {
    try {
      const detail = await rbacService.getRoleDetail(roleId);
      setSelectedRole(detail);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Rollendetails');
    }
  };

  const RoleCard = ({ role, isGlobal }) => {
    const Icon = ROLE_ICONS[role.name] || Shield;
    const colorClass = ROLE_COLORS[role.name] || 'bg-gray-100 text-gray-800';
    
    return (
      <motion.div
        whileHover={{ scale: 1.01 }}
        className="p-4 border rounded-lg cursor-pointer hover:shadow-md transition-shadow bg-white"
        onClick={() => handleRoleClick(role.role_id)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{role.display_name}</span>
              {isGlobal && (
                <Badge variant="outline" className="text-xs">Global</Badge>
              )}
              {role.is_system && (
                <Badge variant="secondary" className="text-xs">System</Badge>
              )}
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{role.description}</p>
          </div>
          <div className="text-right">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {role.permissions_count} Berechtigungen
            </span>
            <ChevronRight className="w-4 h-4 ml-auto mt-1" style={{ color: 'var(--color-text-secondary)' }} />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Shield className="w-5 h-5" />
            Rollen & Berechtigungen
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Übersicht aller verfügbaren Rollen im System
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRoles} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
        </div>
      ) : (
        <>
          {/* Global Roles */}
          <Card style={{ borderColor: 'var(--color-border)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Crown className="w-4 h-4 text-amber-500" />
                Betreiber-Rollen (Global)
              </CardTitle>
              <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
                Systemweite Rollen für Glasmatic-Mitarbeiter
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {roles.global_roles?.map(role => (
                <RoleCard key={role.role_id} role={role} isGlobal={true} />
              ))}
            </CardContent>
          </Card>

          {/* Tenant Roles */}
          <Card style={{ borderColor: 'var(--color-border)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Users className="w-4 h-4 text-blue-500" />
                Mandanten-Rollen
              </CardTitle>
              <CardDescription style={{ color: 'var(--color-text-secondary)' }}>
                Rollen für Kunden innerhalb ihrer Mandanten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {roles.tenant_roles?.map(role => (
                <RoleCard key={role.role_id} role={role} isGlobal={false} />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {/* Role Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRole && (
                <>
                  {React.createElement(ROLE_ICONS[selectedRole.name] || Shield, { className: "w-5 h-5" })}
                  {selectedRole.display_name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedRole?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRole && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant={selectedRole.scope === 'global' ? 'default' : 'secondary'}>
                  {selectedRole.scope === 'global' ? 'Global' : 'Mandant'}
                </Badge>
                {selectedRole.is_system && <Badge variant="outline">System-Rolle</Badge>}
              </div>

              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Berechtigungen ({selectedRole.permissions?.length || 0})
                </h4>
                <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                  {selectedRole.permissions_detail?.map(perm => (
                    <div 
                      key={perm.permission_id}
                      className="flex items-center justify-between p-2 rounded text-sm"
                      style={{ backgroundColor: 'var(--color-background)' }}
                    >
                      <div>
                        <span className="font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {perm.permission_id}
                        </span>
                        <p style={{ color: 'var(--color-text-secondary)' }}>{perm.description}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {perm.category}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const UserRolesPanel = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ global_roles: [], tenant_roles: [] });
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData] = await Promise.all([
        authService.getUsers(),
        rbacService.getRoles()
      ]);
      setUsers(usersData.users || []);
      setRoles(rolesData);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadUserRoles = async (userId) => {
    try {
      const data = await rbacService.getUserRoles(userId);
      setSelectedUser(data);
      setShowAssignDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzerrollen');
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRoleId || !selectedUser) return;
    
    setActionLoading(true);
    try {
      await rbacService.assignRole(
        selectedUser.user_id,
        selectedRoleId,
        'tenant_demo_001' // Default tenant
      );
      toast.success('Rolle zugewiesen');
      await loadUserRoles(selectedUser.user_id);
      setSelectedRoleId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zuweisen der Rolle');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeRole = async (roleId, tenantId) => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      await rbacService.revokeRole(selectedUser.user_id, roleId, tenantId);
      toast.success('Rolle entzogen');
      await loadUserRoles(selectedUser.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entziehen der Rolle');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    return ROLE_COLORS[roleName] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <UserPlus className="w-5 h-5" />
            Benutzer-Rollen
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Rollen für Benutzer verwalten
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
        </div>
      ) : (
        <Card style={{ borderColor: 'var(--color-border)' }}>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-background)' }} className="border-b">
                  <th className="text-left p-4 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Benutzer</th>
                  <th className="text-left p-4 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Globale Rolle</th>
                  <th className="text-left p-4 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Legacy-Rolle</th>
                  <th className="text-right p-4 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.user_id} className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{user.full_name || user.email}</p>
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{user.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      {user.global_role ? (
                        <Badge className={getRoleBadgeColor(user.global_role)}>
                          {user.global_role}
                        </Badge>
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)' }}>-</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{user.role}</Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => loadUserRoles(user.user_id)}
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Rollen verwalten
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* User Roles Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Rollen für {selectedUser?.email}
            </DialogTitle>
            <DialogDescription>
              Verwalten Sie die Rollen dieses Benutzers
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              {/* Global Role */}
              {selectedUser.global_role && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Globale Rolle</h4>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-amber-600" />
                      <span className="font-medium">{selectedUser.global_role_display}</span>
                    </div>
                    <Badge className="bg-amber-100 text-amber-800">Global</Badge>
                  </div>
                </div>
              )}

              {/* Tenant Roles */}
              <div>
                <h4 className="text-sm font-medium mb-2">Mandanten-Rollen</h4>
                {selectedUser.tenant_roles?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUser.tenant_roles.map(tr => (
                      <div key={`${tr.tenant_id}-${tr.role_id}`}>
                        <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                          Mandant: {tr.tenant_name || tr.tenant_id}
                        </p>
                        {tr.roles?.map(role => (
                          <div 
                            key={role.role_id}
                            className="flex items-center justify-between p-2 rounded"
                            style={{ backgroundColor: 'var(--color-background)' }}
                          >
                            <span>{role.display_name}</span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRevokeRole(role.role_id, tr.tenant_id)}
                              disabled={actionLoading}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm p-3 rounded" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-background)' }}>
                    Keine Mandanten-Rollen zugewiesen
                  </p>
                )}
              </div>

              {/* Assign New Role */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-2">Neue Rolle zuweisen</h4>
                <div className="flex gap-2">
                  <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                    <SelectTrigger className="flex-1" style={{ backgroundColor: 'white' }}>
                      <SelectValue placeholder="Rolle auswählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <optgroup label="Globale Rollen">
                        {roles.global_roles?.map(role => (
                          <SelectItem key={role.role_id} value={role.role_id}>
                            {role.display_name} (Global)
                          </SelectItem>
                        ))}
                      </optgroup>
                      <optgroup label="Mandanten-Rollen">
                        {roles.tenant_roles?.map(role => (
                          <SelectItem key={role.role_id} value={role.role_id}>
                            {role.display_name}
                          </SelectItem>
                        ))}
                      </optgroup>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleAssignRole}
                    disabled={!selectedRoleId || actionLoading}
                    style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default { RolesPanel, UserRolesPanel };
